import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Tabs, Tab, Stack, Button, TextField,
  Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Alert, Switch, FormControlLabel, IconButton,
  CircularProgress, Tooltip, Card, CardContent, Avatar, Snackbar, Divider
} from '@mui/material';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import { api } from '../services/api';
import { PRIMARY, NAV_BG } from '../theme';

const moneyFmt = (n, cur) =>
  n == null ? '—' : `${cur ? cur + ' ' : ''}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TenantDetailPage() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [fees, setFees] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState(0);
  const [feeOpen, setFeeOpen] = useState(false);
  const [snack, setSnack] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/api/tenants/${id}`),
      api.get(`/api/tenants/${id}/fees`).catch(() => ({ data: [] })),
      api.get(`/api/tenants/${id}/fees/audit`).catch(() => ({ data: [] }))
    ]);
    setT(a.data); setFees(b.data); setAudit(c.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!t) return <Stack alignItems="center" mt={8}><CircularProgress /></Stack>;

  const setStatus = async (active) => {
    setBusy(true);
    try {
      await api.post(`/api/tenants/${id}/${active ? 'reactivate' : 'suspend'}`,
        active ? {} : { reason: 'Suspended by reseller' });
      await load();
      setSnack({ kind: 'success', msg: active ? 'Tenant reactivated.' : 'Tenant suspended.' });
    } catch (e) {
      setSnack({ kind: 'error', msg: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>{t.name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Chip
              size="small"
              icon={t.isActive ? <VerifiedRoundedIcon sx={{ fontSize: 14 }} /> : <BlockRoundedIcon sx={{ fontSize: 14 }} />}
              label={t.isActive ? 'Active' : 'Suspended'}
              color={t.isActive ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            {t.domainName && <Typography variant="body2" color="text.secondary">{t.domainName}</Typography>}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {t.isActive ? (
            <Button variant="outlined" color="warning" startIcon={<BlockRoundedIcon />} disabled={busy} onClick={() => setStatus(false)}>Suspend</Button>
          ) : (
            <Button variant="outlined" color="success" startIcon={<VerifiedRoundedIcon />} disabled={busy} onClick={() => setStatus(true)}>Reactivate</Button>
          )}
          <Button variant="contained" onClick={() => setFeeOpen(true)}>Set Monthly Fee</Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Info label="Domain" value={t.domainName} />
          <Info label="Industry" value={t.industry} />
          <Info label="Contact email" value={t.contactEmail} />
          <Info label="Plan" value={t.subscriptionPlan} />
          <Info label="Current fee" value={moneyFmt(t.currentMonthlyFee, t.currencyCode)} />
          <Info label="Created" value={t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'} />
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid rgba(15,23,42,0.08)', px: 1 }}
        >
          <Tab label="KPIs" />
          <Tab label="Users" />
          <Tab label="Modules" />
          <Tab label="Fee history" />
          <Tab label="Audit log" />
        </Tabs>
        <Box p={2.5}>
          {tab === 0 && <KpisTab tenantId={id} currency={t.currencyCode || 'ZAR'} />}
          {tab === 1 && <UsersTab tenantId={id} onToast={setSnack} />}
          {tab === 2 && <ModulesTab tenantId={id} onToast={setSnack} />}
          {tab === 3 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Effective from</TableCell><TableCell>Effective to</TableCell><TableCell>Amount</TableCell><TableCell>Notes</TableCell></TableRow></TableHead>
              <TableBody>
                {fees.map(f => (
                  <TableRow key={f.id}>
                    <TableCell>{f.effectiveFrom}</TableCell>
                    <TableCell>{f.effectiveTo || '—'}</TableCell>
                    <TableCell>{f.currencyCode} {Number(f.amount).toFixed(2)}</TableCell>
                    <TableCell>{f.notes || ''}</TableCell>
                  </TableRow>
                ))}
                {fees.length === 0 && <TableRow><TableCell colSpan={4} align="center"><Typography variant="body2" color="text.secondary">No fee history</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          {tab === 4 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>When</TableCell><TableCell>By</TableCell><TableCell>Action</TableCell><TableCell>Old</TableCell><TableCell>New</TableCell></TableRow></TableHead>
              <TableBody>
                {audit.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{new Date(a.changedAt).toLocaleString()}</TableCell>
                    <TableCell>{a.changedBy || ''}</TableCell>
                    <TableCell>{a.action}</TableCell>
                    <TableCell>{a.oldAmount ?? ''}</TableCell>
                    <TableCell>{a.newAmount ?? ''}</TableCell>
                  </TableRow>
                ))}
                {audit.length === 0 && <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">No audit entries</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>

      <SetFeeDialog open={feeOpen} onClose={() => setFeeOpen(false)} onSaved={() => { setFeeOpen(false); load(); }} tenantId={id} currency={t.currencyCode || 'ZAR'} />

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {snack && <Alert severity={snack.kind} onClose={() => setSnack(null)} sx={{ width: '100%' }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}

function Info({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ mt: 0.25 }}>{value || '—'}</Typography>
    </Grid>
  );
}

// ===================== KPIs tab =====================
function KpisTab({ tenantId, currency }) {
  const [k, setK] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/api/tenants/${tenantId}/kpis`)
      .then(r => alive && setK(r.data))
      .catch(e => alive && setErr(e?.response?.status === 403 ? 'You do not have permission to view tenant metrics.' : 'Failed to load metrics.'));
    return () => { alive = false; };
  }, [tenantId]);

  if (err) return <Alert severity="info">{err}</Alert>;
  if (!k) return <Stack alignItems="center" py={4}><CircularProgress /></Stack>;

  const cards = [
    { tone: 'primary', icon: <PolicyRoundedIcon />, label: 'Active Policies', value: k.totalActivePolicies, sub: `${k.newPoliciesThisMonth} new this month` },
    { tone: 'success', icon: <PaidRoundedIcon />,   label: 'Total Premium',   value: moneyFmt(k.totalPremium, currency), sub: `Avg ${moneyFmt(k.averagePremiumPerPolicy, currency)}` },
    { tone: 'warning', icon: <HealthAndSafetyRoundedIcon />, label: 'Claims', value: k.totalClaims, sub: `${Number(k.claimsSuccessRate || 0).toFixed(1)}% success` },
    { tone: 'info',    icon: <GroupsRoundedIcon />, label: 'Users',           value: k.userCount, sub: `${k.activeUsers} active` }
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Operational metrics</Typography>
        {k.asOf && <Chip size="small" label={`As of ${k.asOf}`} sx={{ bgcolor: 'rgba(11,34,79,0.06)', color: PRIMARY, fontWeight: 600 }} />}
      </Stack>
      <Grid container spacing={2} alignItems="stretch">
        {cards.map((c, i) => <Grid item xs={12} sm={6} md={3} key={i}><KpiCard {...c} /></Grid>)}
      </Grid>
      <Grid container spacing={2} mt={0.5}>
        <DetailRow label="Renewal rate" value={`${Number(k.renewalRate || 0).toFixed(1)}%`} />
        <DetailRow label="Customer retention" value={`${Number(k.customerRetentionRate || 0).toFixed(1)}%`} />
        <DetailRow label="Avg settlement days" value={k.averageSettlementDays} />
        <DetailRow label="Claims ratio" value={`${Number(k.claimsRatio || 0).toFixed(2)}%`} />
        <DetailRow label="Health score" value={`${k.healthScore} / 100`} />
        <DetailRow label="New users / month" value={k.newUsersThisMonth} />
      </Grid>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Grid item xs={6} sm={4} md={2}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 600, color: NAV_BG }}>{value}</Typography>
    </Grid>
  );
}

const PALETTES = {
  primary: { from: '#0B224F', to: '#1B3A78' },
  success: { from: '#0F766E', to: '#14B8A6' },
  warning: { from: '#B45309', to: '#F59E0B' },
  info:    { from: '#1D4ED8', to: '#3B82F6' }
};

function KpiCard({ tone = 'primary', icon, label, value, sub }) {
  const p = PALETTES[tone];
  return (
    <Card elevation={0} sx={{
      height: 130, position: 'relative', overflow: 'hidden', borderRadius: 3,
      border: '1px solid rgba(11,34,79,0.08)',
      boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.10)',
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${p.from}, ${p.to})`
      }
    }}>
      <CardContent sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" height="100%">
          <Stack spacing={0.4} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: 11 }}>{label}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAV_BG, lineHeight: 1.15, wordBreak: 'break-word' }}>{value ?? '—'}</Typography>
            {sub && <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 'auto' }}>{sub}</Typography>}
          </Stack>
          <Avatar variant="rounded" sx={{
            width: 40, height: 40, borderRadius: 2, flexShrink: 0,
            background: `linear-gradient(135deg, ${p.from}, ${p.to})`,
            boxShadow: `0 8px 20px -8px ${p.from}`
          }}>{icon}</Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ===================== Users tab =====================
function UsersTab({ tenantId, onToast }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState({});
  const [pwOpen, setPwOpen] = useState(null);

  const load = () => {
    setErr(null); setRows(null);
    api.get(`/api/tenants/${tenantId}/users`)
      .then(r => setRows(r.data))
      .catch(e => setErr(e?.response?.status === 403 ? 'You do not have permission to view tenant users.' : 'Failed to load users.'));
  };
  useEffect(load, [tenantId]);

  const act = async (userId, fn, okMsg) => {
    setBusy(b => ({ ...b, [userId]: true }));
    try { await fn(); onToast({ kind: 'success', msg: okMsg }); load(); }
    catch (e) { onToast({ kind: 'error', msg: e?.response?.data?.error || e.message }); }
    finally { setBusy(b => ({ ...b, [userId]: false })); }
  };

  const resetPassword = async () => {
    try {
      const r = await api.post(`/api/tenants/${tenantId}/users/${pwOpen.userId}/reset-password`, { newPassword: null, mustChange: true });
      setPwOpen({ ...pwOpen, tempPassword: r.data.temporaryPassword });
      onToast({ kind: 'success', msg: 'Password reset. Share the temporary password securely.' });
    } catch (e) { onToast({ kind: 'error', msg: e?.response?.data?.error || e.message }); }
  };

  if (err) return <Alert severity="info">{err}</Alert>;
  if (!rows) return <Stack alignItems="center" py={4}><CircularProgress /></Stack>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tenant users <Typography component="span" variant="body2" color="text.secondary">({rows.length})</Typography>
        </Typography>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last login</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(u => (
            <TableRow key={u.id} hover>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.fullName || '—'}</TableCell>
              <TableCell><Chip size="small" label={u.role} /></TableCell>
              <TableCell>
                <Chip size="small" label={u.isActive ? 'Active' : 'Inactive'} color={u.isActive ? 'success' : 'default'} variant={u.isActive ? 'filled' : 'outlined'} />
              </TableCell>
              <TableCell>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</TableCell>
              <TableCell align="right">
                <Tooltip title="Reset password">
                  <span>
                    <IconButton size="small" disabled={!!busy[u.id]} onClick={() => setPwOpen({ userId: u.id, email: u.email })}>
                      <LockResetRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {u.isActive ? (
                  <Button size="small" color="warning" disabled={!!busy[u.id]}
                    onClick={() => act(u.id, () => api.post(`/api/tenants/${tenantId}/users/${u.id}/deactivate`), 'User deactivated.')}>
                    Deactivate
                  </Button>
                ) : (
                  <Button size="small" color="success" disabled={!!busy[u.id]}
                    onClick={() => act(u.id, () => api.post(`/api/tenants/${tenantId}/users/${u.id}/activate`), 'User activated.')}>
                    Activate
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">No users in this tenant yet.</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>

      <Dialog open={!!pwOpen} onClose={() => setPwOpen(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          {!pwOpen?.tempPassword ? (
            <Stack spacing={1} mt={1}>
              <Typography>Reset the password for <b>{pwOpen?.email}</b>?</Typography>
              <Typography variant="body2" color="text.secondary">A secure temporary password will be generated and the user must change it on next login.</Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5} mt={1}>
              <Alert severity="success">Password reset successfully. Share this with the user securely — it will not be shown again.</Alert>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600 }}>{pwOpen.tempPassword}</Typography>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(pwOpen.tempPassword)}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!pwOpen?.tempPassword ? (
            <>
              <Button onClick={() => setPwOpen(null)}>Cancel</Button>
              <Button variant="contained" onClick={resetPassword}>Reset password</Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setPwOpen(null)}>Done</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ===================== Modules tab =====================
function ModulesTab({ tenantId, onToast }) {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErr(null); setRows(null); setDirty({});
    api.get(`/api/tenants/${tenantId}/modules`)
      .then(r => setRows(r.data))
      .catch(e => setErr(e?.response?.status === 403 ? 'You do not have permission to view tenant modules.' : 'Failed to load modules.'));
  };
  useEffect(load, [tenantId]);

  const toggle = (moduleId, isEnabled) => setDirty(d => ({ ...d, [moduleId]: isEnabled }));
  const effectiveEnabled = (m) => (m.moduleId in dirty ? dirty[m.moduleId] : m.isEnabled);
  const pendingCount = Object.keys(dirty).filter(id => {
    const orig = rows?.find(r => r.moduleId === id)?.isEnabled;
    return orig !== dirty[id];
  }).length;

  const save = async () => {
    setSaving(true);
    try {
      const assignments = Object.entries(dirty).map(([moduleId, isEnabled]) => ({ moduleId, isEnabled }));
      const r = await api.put(`/api/tenants/${tenantId}/modules`, { assignments });
      setRows(r.data); setDirty({});
      onToast({ kind: 'success', msg: 'Module assignments updated.' });
    } catch (e) { onToast({ kind: 'error', msg: e?.response?.data?.error || e.message }); }
    finally { setSaving(false); }
  };

  if (err) return <Alert severity="info">{err}</Alert>;
  if (!rows) return <Stack alignItems="center" py={4}><CircularProgress /></Stack>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Modules <Typography component="span" variant="body2" color="text.secondary">({rows.filter(r => effectiveEnabled(r)).length} of {rows.length} enabled)</Typography>
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" disabled={!pendingCount || saving} onClick={() => setDirty({})}>Discard</Button>
          <Button size="small" variant="contained" disabled={!pendingCount || saving} onClick={save}>{saving ? 'Saving…' : `Save (${pendingCount})`}</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      <Stack spacing={1}>
        {rows.map(m => (
          <Paper key={m.moduleId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box flex={1} minWidth={0}>
              <Typography sx={{ fontWeight: 600 }}>{m.name}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{m.description || m.code}</Typography>
            </Box>
            {!m.isModuleActive && <Chip size="small" label="Inactive in catalog" />}
            <FormControlLabel
              control={<Switch checked={effectiveEnabled(m)} onChange={(e) => toggle(m.moduleId, e.target.checked)} disabled={!m.isModuleActive} />}
              label={effectiveEnabled(m) ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
            />
          </Paper>
        ))}
        {rows.length === 0 && <Typography variant="body2" color="text.secondary">No modules in the catalog.</Typography>}
      </Stack>
    </Box>
  );
}

// ===================== Fee dialog =====================
function SetFeeDialog({ open, onClose, onSaved, tenantId, currency }) {
  const [amount, setAmount] = useState('');
  const [cur, setCur] = useState(currency);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/tenants/${tenantId}/fees`, { amount: Number(amount), currencyCode: cur, effectiveFrom: from, notes });
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Set monthly fee</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <TextField label="Currency" value={cur} onChange={(e) => setCur(e.target.value)} />
          <TextField label="Effective from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !amount}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
